import ReactGA from 'react-ga4';

// Initialize Google Analytics
export const initGA = (measurementId) => {
  if (!measurementId) {
    console.warn('Google Analytics Measurement ID not provided');
    return;
  }
  
  ReactGA.initialize(measurementId, {
    gaOptions: {
      anonymizeIp: true // Privacy-friendly
    }
  });
  
  console.log('Google Analytics initialized');
};

// Track page views
export const logPageView = (path) => {
  ReactGA.send({ hitType: 'pageview', page: path });
};

// Track custom events
export const logEvent = (category, action, label = '') => {
  ReactGA.event({
    category: category,
    action: action,
    label: label
  });
};

// Track product views
export const trackProductView = (productName, productId, category) => {
  if (!productId || !productName) return;
  
  logEvent('Product', 'View', `${productName} (${productId})`);
  
  // Enhanced ecommerce tracking
  ReactGA.event('view_item', {
    currency: 'USD',
    items: [{
      item_id: String(productId),
      item_name: productName,
      item_category: category
    }]
  });
};

// Track add to cart
export const trackAddToCart = (product, quantity = 1) => {
  if (!product || !product.id) return;
  
  const price = product.finalPrice || product.price || 0;
  
  logEvent('Cart', 'Add', `${product.name} x${quantity}`);
  
  ReactGA.event('add_to_cart', {
    currency: 'USD',
    value: price * quantity,
    items: [{
      item_id: String(product.id),
      item_name: product.name,
      item_category: product.categoryId,
      item_variant: product.selectedStorage && product.selectedColor 
        ? `${product.selectedStorage} - ${product.selectedColor}` 
        : undefined,
      price: price,
      quantity: quantity
    }]
  });
};

// Track remove from cart - FIXED VERSION
export const trackRemoveFromCart = (product) => {
  // Safety checks
  if (!product) {
    console.warn('trackRemoveFromCart: product is undefined');
    return;
  }
  
  if (!product.id) {
    console.warn('trackRemoveFromCart: product.id is undefined');
    return;
  }
  
  const price = product.finalPrice || product.price || 0;
  const quantity = product.quantity || 1;
  
  logEvent('Cart', 'Remove', product.name || 'Unknown Product');
  
  ReactGA.event('remove_from_cart', {
    currency: 'USD',
    value: price * quantity,
    items: [{
      item_id: String(product.id),
      item_name: product.name || 'Unknown Product',
      quantity: quantity
    }]
  });
};

// Track quantity update
export const trackQuantityUpdate = (product, oldQty, newQty) => {
  if (!product || !product.id) return;
  
  const action = newQty > oldQty ? 'Increase' : 'Decrease';
  logEvent('Cart', `Quantity ${action}`, `${product.name}: ${oldQty} → ${newQty}`);
};

// Track checkout
export const trackBeginCheckout = (cart, total) => {
  if (!cart || cart.length === 0) return;
  
  logEvent('Checkout', 'Begin', `Total: $${total} (${cart.length} items)`);
  
  ReactGA.event('begin_checkout', {
    currency: 'USD',
    value: total,
    items: cart.map(item => ({
      item_id: String(item.id || 'unknown'),
      item_name: item.name || 'Unknown Product',
      item_category: item.categoryId,
      item_variant: item.selectedStorage && item.selectedColor 
        ? `${item.selectedStorage} - ${item.selectedColor}` 
        : undefined,
      price: item.finalPrice || item.price || 0,
      quantity: item.quantity || 1
    }))
  });
};

// Track category filter
export const trackCategoryFilter = (categoryName, categoryId) => {
  if (!categoryName) return;
  
  logEvent('Navigation', 'Filter Category', categoryName);
  
  ReactGA.event('view_item_list', {
    item_list_name: categoryName,
    item_list_id: categoryId || categoryName
  });
};

// Track search
export const trackSearch = (searchTerm) => {
  if (!searchTerm) return;
  
  logEvent('Search', 'Query', searchTerm);
  
  ReactGA.event('search', {
    search_term: searchTerm
  });
};

// Track clicks
export const trackClick = (element, label) => {
  logEvent('Engagement', 'Click', `${element}: ${label}`);
};

// Track errors
export const trackError = (errorMessage, errorLocation) => {
  logEvent('Error', errorLocation, errorMessage);
};

// Track timing
export const trackTiming = (category, variable, value) => {
  logEvent('Timing', category, `${variable}: ${value}ms`);
};

// Named export object - FIXED FOR BUILD
const analytics = {
  initGA,
  logPageView,
  logEvent,
  trackProductView,
  trackAddToCart,
  trackRemoveFromCart,
  trackQuantityUpdate,
  trackBeginCheckout,
  trackCategoryFilter,
  trackSearch,
  trackClick,
  trackError,
  trackTiming
};

export default analytics;